import React from "react";
import { ChevronDown } from "lucide-react";
import {
  SquareFunction,
  ToggleLeft,
  Hash,
  DecimalsArrowRight,
  Calendar,
  Type,
  List,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import type { SilaeVariable } from "@/types/silae";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

/** Configuration des icônes par type de variable */
const TYPE_ICONS = {
  function: SquareFunction,
  boolean: ToggleLeft,
  int: Hash,
  double: DecimalsArrowRight,
  date: Calendar,
  string: Type,
  selection: List,
  default: Type,
};

/**
 * Récupère l'icône appropriée pour un type de variable
 */
const getTypeIcon = (type: string | undefined) => {
  const IconComponent =
    TYPE_ICONS[type as keyof typeof TYPE_ICONS] || TYPE_ICONS.default;
  return <IconComponent className="h-4 w-4" />;
};

/**
 * Interface pour le composant VariableSelector
 */
export interface VariableSelectorProps {
  selectedVariable: SilaeVariable | null;
  onVariableSelect: (variable: SilaeVariable) => void;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  frequentlyUsedVariables: SilaeVariable[];
  filteredVariables: SilaeVariable[];
  className?: string;
  hasLabel?: boolean;
  placeholder?: string;
}

/**
 * Composant pour le sélecteur de variables SILAE
 */
export const VariableSelector: React.FC<VariableSelectorProps> = ({
  selectedVariable,
  onVariableSelect,
  searchTerm,
  setSearchTerm,
  open,
  setOpen,
  frequentlyUsedVariables,
  filteredVariables,
  className,
  hasLabel = true,
  placeholder,
}) => {
  const { t } = useTranslation();

  return (
    <div className={cn("space-y-2", className)}>
      {hasLabel && <Label>{t("variableSelector.label")}</Label>}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="justify-between h-9 text-left font-normal w-full"
          >
            <div className="flex items-center gap-2 min-w-0">
              {selectedVariable && getTypeIcon(selectedVariable.type)}
              <span className="truncate">
                {selectedVariable
                  ? `${selectedVariable.label || selectedVariable.code}`
                  : placeholder || t("variableSelector.placeholder")}
              </span>
            </div>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-[600px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={t("variableSelector.searchPlaceholder")}
              value={searchTerm}
              onValueChange={setSearchTerm}
            />
            <CommandList>
              <CommandEmpty>{t("variableSelector.noResults")}</CommandEmpty>

              {/* Variables fréquemment utilisées */}
              {frequentlyUsedVariables.length > 0 && searchTerm === "" && (
                <CommandGroup heading={t("variableSelector.isFrequentlyUsed")}>
                  {frequentlyUsedVariables.map((variable) => (
                    <CommandItem
                      key={variable.code}
                      value={variable.code}
                      onSelect={() => {
                        onVariableSelect(variable);
                        setOpen(false);
                      }}
                      className="flex items-start gap-3 p-3"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {getTypeIcon(variable.type)}
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm truncate">
                            {variable.label || variable.code}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {variable.code}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {variable.definition}
                          </div>
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {/* Résultats de recherche */}
              {filteredVariables.length > 0 && (
                <CommandGroup
                  heading={
                    searchTerm
                      ? `${t("variableSelector.searchResults")} (${filteredVariables.length})`
                      : `${t("variableSelector.allVariables")} (${filteredVariables.length})`
                  }
                >
                  {filteredVariables.slice(0, 50).map((variable) => (
                    <CommandItem
                      key={variable.code}
                      value={variable.code}
                      onSelect={() => {
                        onVariableSelect(variable);
                        setOpen(false);
                      }}
                      className="flex items-start gap-3 p-3"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {getTypeIcon(variable.type)}
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm truncate">
                            {variable.label || variable.code}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {variable.code}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {variable.definition}
                          </div>
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                  {filteredVariables.length > 50 && (
                    <div className="p-3 text-xs text-muted-foreground text-center">
                      {t("variableSelector.moreResults", {
                        count: filteredVariables.length - 50,
                      })}
                    </div>
                  )}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
